import type {
  CarriersVehicle,
  CarriersLocation,
  CarriersInsuranceHistory,
  RatedVehicleCoverages,
} from 'api/quote-marketplace';
import { getMatchStatus } from './coverageMatchStatus';
import { getAllAutoCoverageRows } from '../constants/autoCarrierCoverages';
import {
  CORE_COVERAGE_ROWS,
  INDICATOR_ROWS,
  EXTENDED_COVERAGE_ROWS,
  DISCOUNT_ROWS,
  type CoverageRowConfig,
  type DiscountRowConfig,
  type DiscountLocation,
} from '../constants/habitationalCarrierCoverages';

export interface MatchScore {
  matched: number;
  requested: number;
}

export const getAutoCoverageMatchCount = (
  vehicle: CarriersVehicle,
  ratedCoverages: RatedVehicleCoverages | null | undefined,
): MatchScore => {
  const rows = getAllAutoCoverageRows();
  let matched = 0;
  let requested = 0;

  for (const row of rows) {
    if (
      !row.isRequested ||
      !row.getRawRequestedValue ||
      !row.getRawRatedValue
    ) {
      continue;
    }

    const isRequested = row.isRequested(vehicle.coverages, vehicle);
    if (!isRequested) {
      continue;
    }

    requested += 1;

    if (!ratedCoverages) {
      continue;
    }

    const rawRequested = row.getRawRequestedValue(vehicle.coverages, vehicle);
    const rawRated = row.getRawRatedValue(ratedCoverages);
    const status = getMatchStatus(rawRequested, rawRated, true);

    if (status === 'match' || status === 'alternative') {
      matched += 1;
    }
  }

  return { matched, requested };
};

export const getAutoTotalMatchCount = (
  vehicles: CarriersVehicle[],
  ratedVehicles: RatedVehicleCoverages[] | null | undefined,
): MatchScore => {
  let totalMatched = 0;
  let totalRequested = 0;

  vehicles.forEach((vehicle, index) => {
    const rated = ratedVehicles?.[index] ?? null;
    const score = getAutoCoverageMatchCount(vehicle, rated);
    totalMatched += score.matched;
    totalRequested += score.requested;
  });

  return { matched: totalMatched, requested: totalRequested };
};

const getPropertyCoverageRowScore = (
  row: CoverageRowConfig,
  location: CarriersLocation,
  ratedCoverages: Record<string, unknown> | null | undefined,
): { matched: number; requested: number } => {
  if (!row.isRequested || !row.getRawRequestedValue || !row.getRawRatedValue) {
    return { matched: 0, requested: 0 };
  }

  const locationData = {
    building_coverage: location.building_coverage,
    outbuildings: location.outbuildings,
    contents: location.contents,
  };

  const isRequested = row.isRequested(location.coverages, locationData);
  if (!isRequested) {
    return { matched: 0, requested: 0 };
  }

  if (!ratedCoverages) {
    return { matched: 0, requested: 1 };
  }

  const rawRequested = row.getRawRequestedValue(
    location.coverages,
    locationData,
  );
  const rawRated = row.getRawRatedValue(ratedCoverages);
  const status = getMatchStatus(rawRequested, rawRated, true);

  const matched = status === 'match' || status === 'alternative' ? 1 : 0;
  return { matched, requested: 1 };
};

const getPropertyDiscountRowScore = (
  row: DiscountRowConfig,
  location: CarriersLocation,
  insuranceHistory: CarriersInsuranceHistory,
  ratedCoverages: Record<string, unknown> | null | undefined,
): { matched: number; requested: number } => {
  const discountLocation: DiscountLocation = {
    burglar_alarm: location.burglar_alarm,
    fire_alarm: location.fire_alarm,
    number_of_mortgages: location.number_of_mortgages,
    claims_count: location.claims_count,
  };

  const isRequested = row.isRequested(discountLocation, insuranceHistory);
  if (!isRequested) {
    return { matched: 0, requested: 0 };
  }

  if (!ratedCoverages) {
    return { matched: 0, requested: 1 };
  }

  const rawRequested = row.getRawRequestedValue(
    discountLocation,
    insuranceHistory,
  );
  const rawRated = row.getRawRatedValue(ratedCoverages);
  const status = getMatchStatus(rawRequested, rawRated, true);

  const matched = status === 'match' || status === 'alternative' ? 1 : 0;
  return { matched, requested: 1 };
};

export const getPropertyCoverageMatchCount = (
  location: CarriersLocation,
  insuranceHistory: CarriersInsuranceHistory,
  ratedCoverages: Record<string, unknown> | null | undefined,
): MatchScore => {
  let matched = 0;
  let requested = 0;

  const allCoverageRows: CoverageRowConfig[] = [
    ...CORE_COVERAGE_ROWS,
    ...INDICATOR_ROWS,
    ...EXTENDED_COVERAGE_ROWS,
  ];

  for (const row of allCoverageRows) {
    const score = getPropertyCoverageRowScore(row, location, ratedCoverages);
    matched += score.matched;
    requested += score.requested;
  }

  for (const row of DISCOUNT_ROWS) {
    const score = getPropertyDiscountRowScore(
      row,
      location,
      insuranceHistory,
      ratedCoverages,
    );
    matched += score.matched;
    requested += score.requested;
  }

  return { matched, requested };
};

export const getPropertyTotalMatchCount = (
  locations: CarriersLocation[],
  insuranceHistory: CarriersInsuranceHistory,
  ratedLocations: Record<string, unknown>[] | null | undefined,
): MatchScore => {
  let totalMatched = 0;
  let totalRequested = 0;

  locations.forEach((location, index) => {
    const rated = ratedLocations?.[index] ?? null;
    const score = getPropertyCoverageMatchCount(
      location,
      insuranceHistory,
      rated,
    );
    totalMatched += score.matched;
    totalRequested += score.requested;
  });

  return { matched: totalMatched, requested: totalRequested };
};
